import React, { useState, useRef, useEffect } from 'react';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from '../shared/AppSelect';

// Helper to get current financial year start and end
function getCurrentFinancialYearRange() {
  const now = new Date();
  const year = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  const start = new Date(year, 3, 1); // April 1
  const end = new Date(year + 1, 2, 31, 23, 59, 59, 999); // March 31
  return { start, end };
}

/**
 * Compact icon-style filter bar for Reports page
 * Provides quick access to filters with minimal space usage
 */
const CompactReportsFilter = ({ 
  parties = [], 
  dateRange,
  setDateRange,
  financialYear,
  setFinancialYear,
  selectedParty,
  setSelectedParty,
  txnFilter,
  setTxnFilter,
  onFiltersChange,
  showTransactionFilter = false,
  requirePartySelection = false,
  className = ''
}) => {
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  const [showTxnDropdown, setShowTxnDropdown] = useState(false);
  const [partySearchInput, setPartySearchInput] = useState('');
  const [filteredParties, setFilteredParties] = useState(parties);
  const [selectedPartyIndex, setSelectedPartyIndex] = useState(-1);

  const dateDropdownRef = useRef(null);
  const partyDropdownRef = useRef(null);
  const txnDropdownRef = useRef(null);

  // Filter parties based on search input
  useEffect(() => {
    if (partySearchInput.trim() === '') {
      setFilteredParties(parties);
    } else {
      const searchLower = partySearchInput.toLowerCase();
      setFilteredParties(
        parties.filter(party => 
          (party.firmName || party.name || '').toLowerCase().includes(searchLower)
        )
      );
    }
    setSelectedPartyIndex(-1);
  }, [partySearchInput, parties]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setShowDateDropdown(false);
      }
      if (partyDropdownRef.current && !partyDropdownRef.current.contains(event.target)) {
        setShowPartyDropdown(false);
      }
      if (txnDropdownRef.current && !txnDropdownRef.current.contains(event.target)) {
        setShowTxnDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Date presets
  const datePresets = [
    { label: 'Today', value: 'today' },
    { label: 'This Week', value: 'this-week' },
    { label: 'This Month', value: 'this-month' },
    { label: 'This Quarter', value: 'this-quarter' },
    { label: 'This Year', value: 'this-year' },
    { label: 'Last Month', value: 'last-month' },
    { label: 'Last Quarter', value: 'last-quarter' },
    { label: 'Last Year', value: 'last-year' }
  ];

  // Generate financial years - dynamically based on current financial year
  const financialYears = Array.from({ length: 6 }, (_, i) => {
    const currentFY = new Date().getMonth() < 3 ? new Date().getFullYear() - 1 : new Date().getFullYear();
    const year = currentFY - 2 + i; // Show 2 years before and 3 years after current FY
    return { value: year, label: `${year}-${year + 1}` };
  });

  // Transaction mode options
  const txnModeOptions = [
    { value: 'all', label: 'All' },
    { value: 'cash', label: 'Cash' },
    { value: 'bank', label: 'Bank' }
  ];

  // Handle date preset
  const handleDatePreset = (preset) => {
    const now = new Date();
    let start, end;

    switch (preset) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'this-week':
        const dayOfWeek = now.getDay();
        const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        start = new Date(now.getFullYear(), now.getMonth(), diff);
        end = new Date(now.getFullYear(), now.getMonth(), diff + 6, 23, 59, 59);
        break;
      case 'this-month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        break;
      case 'this-quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), quarter * 3, 1);
        end = new Date(now.getFullYear(), (quarter + 1) * 3, 0, 23, 59, 59);
        break;
      case 'this-year':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
        break;
      case 'last-month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      case 'last-quarter':
        const lastQuarter = Math.floor((now.getMonth() - 1) / 3);
        start = new Date(now.getFullYear(), lastQuarter * 3, 1);
        end = new Date(now.getFullYear(), (lastQuarter + 1) * 3, 0, 23, 59, 59);
        break;
      case 'last-year':
        start = new Date(now.getFullYear() - 1, 0, 1);
        end = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
        break;
      default:
        return;
    }

    setDateRange({ start, end });
    if (onFiltersChange) onFiltersChange();
  };

  // Handle party selection
  const handlePartySelect = (partyId) => {
    setSelectedParty(partyId);
    setShowPartyDropdown(false);
    setPartySearchInput('');
    setSelectedPartyIndex(-1);
    if (onFiltersChange) onFiltersChange();
  };

  // Handle transaction mode selection
  const handleTxnModeSelect = (mode) => {
    setTxnFilter(mode);
    setShowTxnDropdown(false);
    if (onFiltersChange) onFiltersChange();
  };

  // Handle keyboard navigation in party dropdown
  const handlePartyKeyDown = (e) => {
    if (!showPartyDropdown) return;

    const totalItems = filteredParties.length + (requirePartySelection ? 0 : 1);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedPartyIndex(prev => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedPartyIndex(prev => prev <= 0 ? totalItems - 1 : prev - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (!requirePartySelection && selectedPartyIndex === 0) {
          handlePartySelect('');
        } else if (requirePartySelection ? selectedPartyIndex >= 0 : selectedPartyIndex > 0) {
          const party = filteredParties[requirePartySelection ? selectedPartyIndex : selectedPartyIndex - 1];
          if (party) handlePartySelect(party.id);
        }
        break;
      case 'Escape':
        setShowPartyDropdown(false);
        setSelectedPartyIndex(-1);
        break;
    }
  };

  // Get selected party name
  const getSelectedPartyName = () => {
    if (!selectedParty) return requirePartySelection ? 'Select Party' : 'All Parties';
    const party = parties.find(p => p.id === selectedParty);
    return party ? (party.firmName || party.name) : 'Unknown Party';
  };

  // Get selected transaction mode name
  const getSelectedTxnModeName = () => {
    const option = txnModeOptions.find(opt => opt.value === txnFilter);
    return option ? option.label : 'All';
  };

  // Get date filter display text
  const getDateFilterText = () => {
    if (!dateRange) return 'All Time';
    const start = dateRange.start.toLocaleDateString();
    const end = dateRange.end.toLocaleDateString();
    return `${start} - ${end}`;
  };

  // Check if filters are active
  const isDateFilterActive = dateRange && (
    dateRange.start.toDateString() !== new Date().toDateString() ||
    dateRange.end.toDateString() !== new Date().toDateString()
  );
  const isPartyFilterActive = selectedParty !== '';
  const isTxnFilterActive = txnFilter !== 'all';

  const activeFilterCount = [isDateFilterActive, isPartyFilterActive, isTxnFilterActive].filter(Boolean).length;

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Compact Filter Bar */}
      <div className="p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          {/* Left side - Filter Icons */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Date Filter */}
            <div className="relative" ref={dateDropdownRef}>
              <button
                onClick={() => setShowDateDropdown(!showDateDropdown)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm ${
                  isDateFilterActive
                    ? 'bg-blue-100 text-blue-800 border border-blue-200 shadow-blue-100'
                    : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 hover:shadow-md'
                }`}
                title="Date Filter"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="max-w-[5.5rem] sm:max-w-32 truncate text-xs sm:text-sm">{getDateFilterText()}</span>
                <svg className={`w-3 h-3 transition-transform duration-200 ${showDateDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Date Dropdown */}
              {showDateDropdown && (
                <div className="absolute top-full left-0 mt-2 report-filter-dropdown bg-white rounded-xl shadow-xl border border-gray-200 z-[60] p-4 sm:p-5 max-h-[min(80dvh,28rem)] overflow-y-auto">
                  <div className="space-y-4">
                    {/* Quick Filters */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Quick Filters</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {datePresets.map((preset) => (
                          <button
                            key={preset.value}
                            onClick={() => {
                              handleDatePreset(preset.value);
                              setShowDateDropdown(false);
                            }}
                            className="px-3 py-2 text-xs rounded-lg transition-all duration-200 bg-gray-50 text-gray-700 border border-gray-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 hover:shadow-sm"
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Date Range */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Custom Range</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input
                          type="date"
                          value={dateRange?.start?.toISOString().split('T')[0] || ''}
                          onChange={(e) => {
                            setDateRange(prev => ({ ...prev, start: new Date(e.target.value) }));
                            if (onFiltersChange) onFiltersChange();
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="From"
                        />
                        <input
                          type="date"
                          value={dateRange?.end?.toISOString().split('T')[0] || ''}
                          onChange={(e) => {
                            setDateRange(prev => ({ ...prev, end: new Date(e.target.value) }));
                            if (onFiltersChange) onFiltersChange();
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="To"
                        />
                      </div>
                    </div>

                    {/* Financial Year */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Financial Year</h4>
                      <AppSelect
                        value={financialYear}
                        onChange={(e) => {
                          const selectedYear = parseInt(e.target.value, 10);
                          console.log('📊 FINANCIAL YEAR FILTER SELECTED:', {
                            selectedYear,
                            financialYearRange: `${selectedYear}-${selectedYear + 1}`,
                          });
                          setFinancialYear(selectedYear);
                          if (onFiltersChange) onFiltersChange();
                        }}
                        aria-label="Financial year"
                        sheetTitle="Financial year"
                        options={financialYears.map((year) => ({ value: year.value, label: year.label }))}
                        triggerClassName={APP_SELECT_FILTER_TRIGGER}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Party Filter */}
            <div className="relative" ref={partyDropdownRef}>
              <button
                onClick={() => setShowPartyDropdown(!showPartyDropdown)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm ${
                  isPartyFilterActive
                    ? 'bg-green-100 text-green-800 border border-green-200 shadow-green-100'
                    : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 hover:shadow-md'
                }`}
                title="Party Filter"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="max-w-[4.5rem] sm:max-w-24 truncate text-xs sm:text-sm">{getSelectedPartyName()}</span>
                <svg className={`w-3 h-3 transition-transform duration-200 ${showPartyDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Party Dropdown */}
              {showPartyDropdown && (
                <div className="absolute top-full left-0 mt-2 report-filter-dropdown bg-white rounded-xl shadow-xl border border-gray-200 z-[60] p-4 sm:p-5 max-h-[min(70dvh,24rem)] overflow-y-auto">
                  <div className="space-y-3">
                    {/* Search Input */}
                    <div className="relative">
                      <input
                        type="text"
                        value={partySearchInput}
                        onChange={(e) => setPartySearchInput(e.target.value)}
                        onKeyDown={handlePartyKeyDown}
                        placeholder="Type to search party..."
                        className="w-full px-3 py-2 pl-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                      <svg className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      {partySearchInput && (
                        <button
                          onClick={() => setPartySearchInput('')}
                          className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 hover:text-gray-600"
                        >
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Party List */}
                    <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                      {!requirePartySelection && (
                      <button
                        onClick={() => handlePartySelect('')}
                        className={`w-full text-left px-3 py-2 rounded-t-md text-sm transition-colors border-b border-gray-100 ${
                          selectedPartyIndex === 0
                            ? 'bg-blue-100 text-blue-800'
                            : !selectedParty
                            ? 'bg-blue-50 text-blue-700 font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          All Parties
                        </div>
                      </button>
                      )}
                      {requirePartySelection && !selectedParty && (
                        <div className="px-3 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100">
                          Select a party to load detailed report
                        </div>
                      )}
                      {filteredParties.map((party, index) => (
                        <button
                          key={party.id}
                          onClick={() => handlePartySelect(party.id)}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                            index === filteredParties.length - 1 ? 'rounded-b-md' : 'border-b border-gray-100'
                          } ${
                            selectedPartyIndex === (requirePartySelection ? index : index + 1)
                              ? 'bg-blue-100 text-blue-800'
                              : selectedParty === party.id
                              ? 'bg-blue-50 text-blue-700 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span className="truncate">{party.firmName || party.name}</span>
                          </div>
                        </button>
                      ))}
                      {filteredParties.length === 0 && partySearchInput && (
                        <div className="text-center text-gray-500 text-sm py-4 px-3">
                          <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          No parties found for "{partySearchInput}"
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Transaction Mode Filter - Only show for Cash Flow report */}
            {showTransactionFilter && (
              <div className="relative" ref={txnDropdownRef}>
                <button
                  onClick={() => setShowTxnDropdown(!showTxnDropdown)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 shadow-sm ${
                    isTxnFilterActive
                      ? 'bg-orange-100 text-orange-800 border border-orange-200 shadow-orange-100'
                      : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 hover:shadow-md'
                  }`}
                  title="Transaction Mode Filter"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2M7 4h10M7 4v16M17 4v16M7 20h10M9 6h6M9 10h6M9 14h6" />
                  </svg>
                  <span className="hidden sm:inline max-w-16 truncate">{getSelectedTxnModeName()}</span>
                  <svg className={`w-3 h-3 transition-transform duration-200 ${showTxnDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Transaction Mode Dropdown */}
                {showTxnDropdown && (
                  <div className="absolute top-full left-0 mt-2 report-filter-dropdown bg-white rounded-xl shadow-xl border border-gray-200 z-[60] p-3">
                    {txnModeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleTxnModeSelect(option.value)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors rounded-md ${
                          txnFilter === option.value
                            ? 'bg-orange-100 text-orange-800 font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Right side - Filter Status */}
          <div className="flex items-center gap-3">
            {/* Active Filter Count */}
            {activeFilterCount > 0 && (
              <div className="flex items-center gap-2">
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1.5 rounded-full shadow-sm">
                  {activeFilterCount} active
                </span>
                <button
                  onClick={() => {
                    setDateRange(getCurrentFinancialYearRange());
                    setSelectedParty('');
                    setTxnFilter('all');
                    if (onFiltersChange) onFiltersChange();
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                  title="Clear All Filters"
                >
                  Clear All
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompactReportsFilter;
